import { useState, useContext, useEffect } from 'react';
import {
    Button,
    Card,
    CardBody,
    Col,
    Row,
    Form,
    FormGroup,
    Label,
    Input,
    FormFeedback,
} from 'reactstrap';
import { useForm, useFieldArray } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import {
    SelectInput,
    TextInput,
    PhoneInput,
    stateOptions,
    formatPhoneNumber,
} from '~/shared/components/form/Inputs';
import {
    nameTitleOptions,
    nameSuffixOptions,
    genderOptions,
    maritalStatusOptions,
    ethnicityOptions,
    languageOptions,
    phoneOptions,
} from '../../shared/config';
import states from '~/shared/states.json';
import { Loading } from '~/shared/components/Loading';
import { CampaignContext } from '~/context/campaign';
import { fetchMyCampaign, putPatient } from '../../services/campaign';
import toast from 'react-hot-toast';
import { CheckBox } from '~/shared/components/form/CheckBox';
import { format, parseISO } from 'date-fns';
import ActionButtons from './ActionButtons';
import moment from 'moment';
import { AdminContext } from '~/context/admin';
import { FaBan } from 'react-icons/fa';
import Autocomplete from 'react-google-autocomplete';
// import { CampaignPhotoUpload } from '~/containers/Campaign/components/CampaignPhotoUpload';

type DefaultEditPatient = {
    title: string;
    suffix: string;
    first_name: string;
    middle_name: string;
    last_name: string;
    nicknames: string;
    update_photo: boolean;
    address_1: string;
    address_2: string;
    city: string;
    state: string;
    zip_code: string;
    gender: string;
    birth_date: string;
    patient_is_deceased: boolean;
    death_date: string;
    marital_status: string;
    ethnicity: string;
    preferred_language: string;
    email: string;
    phone: string;
    use_temporary_address: boolean;
    temporary_address_1: string;
    temporary_address_2: string;
    temporary_city: string;
    temporary_state: string;
    temporary_zip_code: string;
    temporary_country: string;
    temporary_from_date: string;
    temporary_to_date: string;
};

const defaultEditPatient: DefaultEditPatient = {
    title: '',
    suffix: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    nicknames: '',
    update_photo: false,
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: '',
    gender: '',
    birth_date: '',
    patient_is_deceased: false,
    death_date: '',
    marital_status: '',
    ethnicity: '',
    preferred_language: '',
    email: '',
    phone: '',
    use_temporary_address: false,
    temporary_address_1: '',
    temporary_address_2: '',
    temporary_city: '',
    temporary_state: '',
    temporary_zip_code: '',
    temporary_country: '',
    temporary_from_date: '',
    temporary_to_date: '',
};

const apiKey = import.meta.env.VITE_PLACES_API_KEY;

export const EditPatient = () => {
    const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
    const [{ isAdmin, campaignId }, adminDispatch] = useContext(AdminContext);
    const [loading, setLoading] = useState<boolean>(true);
    const [stateInputErrors, setStateInputErrors] = useState({
        state: false,
        temporary_state: false,
    });
    const history = useHistory();
    const {
        control,
        handleSubmit,
        reset,
        setValue,
        getValues,
        watch,
        formState: { errors, isSubmitting },
        setError,
    } = useForm({
        defaultValues: defaultEditPatient,
    });
    const patient_is_deceased = watch('patient_is_deceased');
    const use_temporary_address = watch('use_temporary_address');
    const requiredSelectFields: (keyof DefaultEditPatient)[] = [
        'gender',
        'marital_status',
        'ethnicity',
        'preferred_language',
    ];
    const requiredStateFields: (keyof DefaultEditPatient)[] = ['state'];
    if (use_temporary_address) {
        requiredStateFields.push('temporary_state');
    }

    const { fields, append, remove } = useFieldArray({
        name: 'phone_set',
        control,
    });

    const getCampaign = async () => {
        const resp = await fetchMyCampaign();
        if (resp.detail) {
            if (resp.detail === 'Invalid token.') {
                localStorage.removeItem('token');
                history.push('/login');
            }
        } else {
            campaignDispatch({
                type: 'set_campaign',
                payload: resp,
            });
        }
    };

    useEffect(() => {
        if (!campaign) {
            getCampaign();
        }
    }, []);

    useEffect(() => {
        if (campaign) {
            const { patient } = campaign;
            const person = patient?.person;

            if (person && patient) {
                reset({
                    ...person,
                    ...patient,
                    // Normalize stored numbers (e.g. legacy 508-555-1234) to the
                    // (123) 456-7890 format used by the admin Client Info drawer.
                    phone_set: (person.phone_set || []).map((p: any) => ({
                        ...p,
                        phone: formatPhoneNumber(p?.phone || ''),
                    })),
                });
                setLoading(false);
            }
        }
    }, [campaign]);

    if (!campaign || loading) {
        return <Loading />;
    }
    const onSubmit = async (data: DefaultEditPatient) => {
        toast.loading('Updating profile...');
        
        const phoneCheck = watch('phone');
        if (!phoneCheck || phoneCheck.includes('_')) {
            setError('phone', { message: 'Phone is required' });
        }
        let stateErrorFound = false;
        requiredStateFields.forEach((field) => {
            const errorCheck = watch(field);
            if (
                typeof errorCheck === 'string' &&
                (errorCheck?.toLowerCase() === 'select' || !errorCheck)
            ) {
                stateErrorFound = true;
                setStateInputErrors({ ...stateInputErrors, [field]: true });
            } else setStateInputErrors({ ...stateInputErrors, [field]: false });
        });
        requiredSelectFields.forEach((field) => {
            const errorCheck = watch(field);
            if (
                typeof errorCheck === 'string' &&
                (errorCheck?.toLowerCase() === 'select' || !errorCheck)
            ) {
                setError(field, { message: 'Phone is required' });
            }
        });

        setValue('update_photo', true);

        if (stateErrorFound) {
            return;
        }
        try {
            const resp = await putPatient(data, campaignId);
            toast.dismiss();

            if (resp.detail) {
                toast.error(resp.detail);
            } else {
                campaignDispatch({
                    type: 'set_campaign',
                    payload: resp,
                });

                toast.success('Profile updated');
                if (resp.account_email_sent) {
                    toast.success('Account setup email sent');
                }
                // history.push('/patient/edit');
            }
        } catch (e) {
            toast.dismiss();
        }
    };

    const medProfName = () => {
        const medProf = campaign?.patient?.medical_professional?.person;
        if (!medProf) {
            return '';
        }
        const { first_name, last_name } = medProf;
        return `${first_name} ${last_name}`;
    };

    const insuranceName = () => {
        const { campaignApplication } = campaign;
        return campaignApplication?.ins_company;
    };

    const update_photo = getValues('update_photo');

    const updatePhoto = (update: boolean) => {
        setValue('update_photo', update);
    };

    const getAgreementsStatus = () => {
        const defaultText = 'Not Signed';
        if (!campaign) {
            return defaultText;
        }
        const { campaignApplicationItems } = campaign;
        const agreementItem = campaignApplicationItems.find(
            (x) => x.item_type === 'agreement'
        );
        if (!agreementItem || !agreementItem.completed_date) {
            return defaultText;
        }
        const signedDate = moment(
            agreementItem.completed_date,
            'YYYY-MM-DD'
        ).format('MM/DD/YY');
        return `Signed ${signedDate}` || defaultText;
    };

    const handleInvalid = () => {
        const errorMessages = Object.entries(errors).map(([key, value]) => {
            return value.message?.replace(' is required', '');
        });
        toast.error(`Required fields missing: \n\n` + errorMessages.join('\n'));
    };

    return (
        <div className='container__inner'>
            <div className='mb-5'>
                <Row>
                    <Col xl={6}>
                        <h1 className='h2'>
                            {campaign?.patient?.person?.first_name}'s Profile
                        </h1>
                    </Col>
                    <Col
                        xl={6}
                        className='d-none d-md-flex justify-content-xl-end align-items-start'
                    >
                        <ActionButtons
                            handleSubmit={handleSubmit}
                            onSubmit={onSubmit}
                            isSubmitting={isSubmitting}
                            handleInvalid={handleInvalid}
                        />
                    </Col>
                </Row>
            </div>
            <Row>
                <Col lg='6'>
                    <Form>
                        <Card className='border mb-3'>
                            <CardBody>
                                <h4>Name</h4>
                                <Row>
                                    <Col>
                                        <SelectInput
                                            control={control}
                                            name='title'
                                            label='Title'
                                            options={nameTitleOptions}
                                            errors={errors?.title?.message}
                                            defaultLabel='Select Title'
                                        />
                                    </Col>
                                    <Col>
                                        <SelectInput
                                            control={control}
                                            name='suffix'
                                            label='Suffix'
                                            options={nameSuffixOptions}
                                            errors={errors?.suffix?.message}
                                            defaultLabel='Select Suffix'
                                        />
                                    </Col>
                                </Row>
                                <TextInput
                                    name='first_name'
                                    label='First Name'
                                    control={control}
                                    required={true}
                                    ariaRequired={true}
                                />
                                <TextInput
                                    name='middle_name'
                                    label='Middle Name'
                                    control={control}
                                />
                                <TextInput
                                    name='last_name'
                                    label='Last Name'
                                    control={control}
                                    required={true}
                                    ariaRequired={true}
                                />
                                <TextInput
                                    name='nicknames'
                                    label='Nickname'
                                    control={control}
                                />

                                {/* <Label>Profile Picture</Label>
                <CampaignPhotoUpload updatePhoto={updatePhoto} /> */}
                            </CardBody>
                        </Card>

                        <Card className='border mb-3'>
                            <CardBody>
                                <h4>Demographic Information</h4>
                                <SelectInput
                                    control={control}
                                    name='gender'
                                    label='Gender'
                                    options={genderOptions}
                                    required={true}
                                    ariaRequired={true}
                                    defaultLabel='Select Gender'
                                />
                                <TextInput
                                    name='birth_date'
                                    label='Date of Birth'
                                    control={control}
                                    type='date'
                                    required={true}
                                    ariaRequired={true}
                                />
                                <FormGroup check className='mb-3 ps-0'>
                                    <CheckBox
                                        name='patient_is_deceased'
                                        label='Client is deceased'
                                        value={patient_is_deceased}
                                        onChange={() => {
                                            setValue(
                                                'patient_is_deceased',
                                                !patient_is_deceased
                                            );
                                            setValue('death_date', null);
                                        }}
                                    />
                                </FormGroup>
                                {patient_is_deceased && (
                                    <TextInput
                                        name='death_date'
                                        label='Date of Death'
                                        control={control}
                                        type='date'
                                        required={patient_is_deceased}
                                    />
                                )}
                                <SelectInput
                                    control={control}
                                    name='marital_status'
                                    label='Martial Status'
                                    options={maritalStatusOptions}
                                    required={true}
                                    ariaRequired={true}
                                    defaultLabel='Select Martial Status'
                                />
                                <SelectInput
                                    control={control}
                                    name='ethnicity'
                                    label='With what ethnicity or race does the client identify?'
                                    options={ethnicityOptions}
                                    required={true}
                                    ariaRequired={true}
                                    defaultLabel='Select Ehtnicity'
                                />
                                <SelectInput
                                    control={control}
                                    name='preferred_language'
                                    label='Preferred Language'
                                    options={languageOptions}
                                    defaultLabel='Select Language'
                                />
                            </CardBody>
                        </Card>

                        <Card className='border mb-3'>
                            <CardBody>
                                <h4>Contact Information</h4>
                                <TextInput
                                    name='email'
                                    label='Email Address'
                                    type='email'
                                    control={control}
                                />
                                {fields.map((field, index) => (
                                    <Row
                                        className='align-items-center'
                                        key={field.id}
                                    >
                                        <Col sm={6}>
                                            <PhoneInput
                                                name={`phone_set[${index}].phone`}
                                                label='Phone Number'
                                                control={control}
                                                required={true}
                                                ariaRequired={true}
                                                formatted={true}
                                            />
                                        </Col>
                                        <Col sm={4}>
                                            <SelectInput
                                                control={control}
                                                name={`phone_set[${index}].type`}
                                                label='Type'
                                                options={phoneOptions}
                                                required={true}
                                                ariaRequired={true}
                                            />
                                        </Col>
                                        <Col sm={2}>
                                            <Button
                                                color='link'
                                                className='mt-2 px-0 text-danger'
                                                onClick={() => remove(index)}
                                            >
                                                <FaBan size={18} />
                                            </Button>
                                        </Col>
                                    </Row>
                                ))}
                                <Button
                                    color='link'
                                    className='p-0'
                                    onClick={() =>
                                        append({ phone: '', type: '' })
                                    }
                                >
                                    Add Phone Number
                                </Button>
                            </CardBody>
                        </Card>

                        <Card className='border mb-3'>
                            <CardBody>
                                <h4>Address</h4>
                                <FormGroup className='mb-3'>
                                    <Label>Address Lookup</Label>

                                    <Autocomplete
                                        apiKey={apiKey}
                                        onPlaceSelected={(place) => {
                                            const { address_components } = place;

                                            const streetNumber = address_components.find((x) =>
                                                x.types.includes('street_number')
                                            );
                                            const route = address_components.find((x) =>
                                                x.types.includes('route')
                                            );

                                            const city = address_components.find((x) =>
                                                x.types.includes('locality')
                                            );
                                            const state = address_components.find((x) =>
                                                x.types.includes('administrative_area_level_1')
                                            );
                                            const zip = address_components.find((x) =>
                                                x.types.includes('postal_code')
                                            );

                                            const address1 = streetNumber
                                                ? `${streetNumber.long_name} ${route?.long_name || ''}`
                                                : route?.long_name || '';

                                            setValue('address_1', address1);
                                            setValue('city', city?.long_name);
                                            const matchingState = stateOptions.find(
                                                (x) => x.value === state?.short_name
                                            );
                                            if (matchingState) {
                                                setValue('state', matchingState.value);
                                            }
                                            setValue('zip_code', zip?.long_name);
                                        }}
                                        options={{
                                            types: ['address'],
                                            componentRestrictions: {
                                                country: 'us',
                                            },
                                        }}
                                        className='form-control-md form-control'
                                    />
                                </FormGroup>
                                <TextInput
                                    name='address_1'
                                    label='Street Address 1'
                                    control={control}
                                    required={true}
                                    ariaRequired={true}
                                />
                                <TextInput
                                    name='address_2'
                                    label='Street Address 2'
                                    control={control}
                                />
                                <TextInput
                                    name='city'
                                    label='City'
                                    control={control}
                                    required={true}
                                    ariaRequired={true}
                                />
                                <Row>
                                    <Col>
                                        <FormGroup>
                                            <Label>
                                                State{' '}
                                                <span className='required'>
                                                    *
                                                </span>
                                            </Label>
                                            <Input
                                                type='select'
                                                name='state'
                                                id='state'
                                                value={watch('state') || ''}
                                                defaultValue={getValues('state')}
                                                onChange={(e) => {
                                                    setValue('state', e.target.value, {
                                                        shouldValidate: true,
                                                        shouldDirty: true 
                                                    });
                                                }}
                                                required={true}
                                            >
                                                <option value="">
                                                    Select
                                                </option>
                                                {Object.entries(states).map(
                                                    ([accronym, label], i) => (
                                                        <option
                                                            key={i}
                                                            value={accronym}
                                                        >
                                                            {label}
                                                        </option>
                                                    )
                                                )}
                                            </Input>
                                            {stateInputErrors.state && (
                                                <FormFeedback
                                                    style={{
                                                        display: 'block',
                                                    }}
                                                >
                                                    State is required
                                                </FormFeedback>
                                            )}
                                        </FormGroup>
                                    </Col>
                                    <Col>
                                        <TextInput
                                            name='zip_code'
                                            label='Zip Code'
                                            type='zip'
                                            control={control}
                                            required={true}
                                            ariaRequired={true}
                                        />
                                    </Col>
                                </Row>
                                <FormGroup check className='mb-3 ps-0'>
                                    <CheckBox
                                        name='use_temporary_address'
                                        label='Use Temporary Address'
                                        value={use_temporary_address}
                                        onChange={() => {
                                            setValue(
                                                'use_temporary_address',
                                                !use_temporary_address
                                            );
                                        }}
                                    />
                                </FormGroup>
                                {use_temporary_address && (
                                    <>
                                        <h4>Temporary Address</h4>
                                        <Row>
                                            <Col>
                                                <TextInput
                                                    name='temporary_from_date'
                                                    label='Start Date'
                                                    type='date'
                                                    control={control}
                                                />
                                            </Col>
                                            <Col>
                                                <TextInput
                                                    name='temporary_to_date'
                                                    label='End Date'
                                                    type='date'
                                                    control={control}
                                                />
                                            </Col>
                                        </Row>
                                        <TextInput
                                            name='temporary_address_1'
                                            label='Street Address 1'
                                            control={control}
                                            required={use_temporary_address}
                                        />
                                        <TextInput
                                            name='temporary_address_2'
                                            label='Street Address 2'
                                            control={control}
                                        />
                                        <TextInput
                                            name='temporary_city'
                                            label='City'
                                            control={control}
                                            required={use_temporary_address}
                                        />
                                        <Row>
                                            <Col>
                                                <FormGroup>
                                                    <Label>
                                                        State{' '}
                                                        <span className='required'>
                                                            *
                                                        </span>
                                                    </Label>
                                                    <Input
                                                        type='select'
                                                        name='temporary_state'
                                                        id='temporary_state'
                                                        value={watch(
                                                            'temporary_state'
                                                        )}
                                                        defaultValue={getValues(
                                                            'temporary_state'
                                                        )}
                                                        onChange={(e) =>
                                                            setValue(
                                                                'temporary_state',
                                                                e.target.value
                                                            )
                                                        }
                                                        required={
                                                            use_temporary_address
                                                        }
                                                    >
                                                        <option
                                                            value={undefined}
                                                        >
                                                            Select
                                                        </option>
                                                        {Object.entries(
                                                            states
                                                        ).map(
                                                            (
                                                                [
                                                                    accronym,
                                                                    label,
                                                                ],
                                                                i
                                                            ) => (
                                                                <option
                                                                    key={i}
                                                                    value={
                                                                        accronym
                                                                    }
                                                                >
                                                                    {label}
                                                                </option>
                                                            )
                                                        )}
                                                    </Input>
                                                    {stateInputErrors.temporary_state && (
                                                        <FormFeedback
                                                            style={{
                                                                display:
                                                                    'block',
                                                            }}
                                                        >
                                                            State is required
                                                        </FormFeedback>
                                                    )}
                                                </FormGroup>
                                            </Col>
                                            <Col>
                                                <TextInput
                                                    name='temporary_zip_code'
                                                    label='Zip Code'
                                                    type='zip'
                                                    control={control}
                                                    required={
                                                        use_temporary_address
                                                    }
                                                />
                                            </Col>
                                        </Row>
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </Form>
                </Col>
                <Col lg='6'>
                    <Card className='border mb-3'>
                        <CardBody>
                            <h4>Medical Professional</h4>

                            <Label>Name</Label>
                            <Input
                                disabled={true}
                                readOnly={true}
                                value={medProfName()}
                            />
                            <Button
                                color='link'
                                className='px-0'
                                onClick={() =>
                                    history.push(
                                        '/patient/medical-professional'
                                    )
                                }
                            >
                                Manage Medical Professional
                            </Button>
                            <div className='small text-muted'>
                                Last Updated{' '}
                                {campaign?.patient?.medical_professional
                                    ?.update_date
                                    ? format(
                                        parseISO(
                                            campaign.patient
                                                .medical_professional
                                                .update_date
                                        ),
                                        'MM/dd/yy'
                                    )
                                    : ''}
                            </div>
                        </CardBody>
                    </Card>
                    <Card className='border mb-3'>
                        <CardBody>
                            <h4>Insurance</h4>

                            <Label>Name</Label>
                            <Input
                                disabled={true}
                                readOnly={true}
                                value={insuranceName()}
                            />
                            <Button
                                color='link'
                                className='px-0'
                                onClick={() =>
                                    history.push('/patient/insurance')
                                }
                            >
                                Manage Insurance
                            </Button>
                            <div className='small text-muted'>
                                Last Updated{' '}
                                {campaign?.campaignApplication?.update_date
                                    ? format(
                                        parseISO(
                                            campaign?.campaignApplication
                                                ?.update_date
                                        ),
                                        'MM/dd/yy'
                                    )
                                    : ''}
                            </div>
                        </CardBody>
                    </Card>
                    <Card className='border mb-3'>
                        <CardBody>
                            <h4>Financial Information</h4>

                            <Button
                                color='link'
                                className='px-0'
                                onClick={() =>
                                    history.push('/patient/finances')
                                }
                            >
                                Update Financial Information
                            </Button>
                            <div className='small text-muted'>
                                Last Updated{' '}
                                {campaign?.campaignApplication?.update_date
                                    ? format(
                                        parseISO(
                                            campaign?.campaignApplication
                                                ?.update_date
                                        ),
                                        'MM/dd/yy'
                                    )
                                    : ''}
                            </div>
                        </CardBody>
                    </Card>
                    <Card className='border mb-3'>
                        <CardBody>
                            <h4>Agreements</h4>

                            <Button
                                color='link'
                                className='px-0'
                                onClick={() =>
                                    history.push('/patient/agreements')
                                }
                            >
                                Agreements
                            </Button>
                            <div className='small text-muted'>
                                {getAgreementsStatus()}
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <div
                className='sticky-bottom p-4 bg-white'
                style={{
                    borderTop: '1px solid #0D0106',
                    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.25)',
                }}
            >
                <ActionButtons
                    handleSubmit={handleSubmit}
                    onSubmit={onSubmit}
                    isSubmitting={isSubmitting}
                    handleInvalid={handleInvalid}
                />
            </div>
        </div>
    );
};
